import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { Card, Row, Col, Button, Spin, Modal, Typography, Divider, Form, Input, Upload } from 'antd'
import { ArrowLeftOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons'
import ArticleResponseCard from './ArticleResponseCard'
import CommercialConditionsCard from './CommercialConditionsCard'
import { roundToTwoDecimals } from './utils'
import './SupplierQuotationResponse.css'
import dayjs from 'dayjs'
import { axiosForm } from '../../axiosInstances'
import { AuthContext } from '../auth/AuthContext'

const { Title, Text } = Typography

const SupplierQuotationResponse = () => {
  const { rfqId } = useParams()
  const { token } = useContext(AuthContext)
  const [rfqData, setRfqData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [currencies] = useState([
    { id: 90, name: 'Moroccan Dirham', code: 'MAD' },
    { id: 2, name: 'Euro', code: 'EUR' },
    { id: 3, name: 'US Dollar', code: 'USD' },
  ])
  const [itemResponses, setItemResponses] = useState({})
  const [quoteData, setQuoteData] = useState({
    quote_number: '',
    currency: '',
    currency_name: '',
    payment_at_command: '',
    payment_at_command_days: '',
    payment_at_delivery: '',
    payment_at_delivery_days: '',
    payment_at_receipt: '',
    payment_at_receipt_days: '',
    order_ceiling: '',
    validity_period: '',
    vendor_notes: '',
    tva: 20,
    documents: [],
  })
  const [submitModalVisible, setSubmitModalVisible] = useState(false)
  const [successModalVisible, setSuccessModalVisible] = useState(false)

  useEffect(() => {
    if (!rfqId || !token) return
    setLoading(true)
    axiosForm.get(`/authentication/api/rfq/${rfqId}/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const rfq = res.data
        setRfqData(rfq)
        const initialResponses = {}
        rfq.items.forEach(item => {
          initialResponses[item.id] = {
            id: item.id,
            quoted_unit_price: '',
            quoted_total_price: '',
            is_available: null,
            delivery_time: '',
            vendor_comments: '',
            has_alternative: false,
            alternative_item_name: '',
            alternative_unit_price: '',
            alternative_total_price: '',
            alternative_comments: '',
            alternative_delivery_time: '',
            alternative_reference: '',
            available_quantity: item.quantity,
            alternative_quantity: item.quantity,
            alternative_brand: '',
            alternative_description: '',
          }
        })
        setItemResponses(initialResponses)
        setQuoteData(prev => ({
          ...prev,
          quote_number: `Q-${rfq.vendor_name?.substring(0, 3).toUpperCase()}-${Date.now()}`,
          // Store currency as code (MAD/EUR/USD), not as ID
          currency: rfq.currency_code,
          currency_name: rfq.currency_name,
          payment_at_command: rfq.payment_at_command,
          payment_at_command_days: rfq.payment_at_command_days,
          payment_at_delivery: rfq.payment_at_delivery,
          payment_at_delivery_days: rfq.payment_at_delivery_days,
          payment_at_receipt: rfq.payment_at_receipt,
          payment_at_receipt_days: rfq.payment_at_receipt_days,
          order_ceiling: rfq.order_ceiling,
          validity_period: '30',
          vendor_notes: '',
          tva: rfq.tva || 20,
          documents: [],
        }))
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [rfqId, token])

  const handleItemChange = (itemId, field, value) => {
    setItemResponses(prev => {
      const updated = { ...prev }
      updated[itemId] = { ...updated[itemId], [field]: value }
      if (field === 'quoted_unit_price' || field === 'available_quantity') {
        const unit = parseFloat(
          field === 'quoted_unit_price'
            ? value
            : updated[itemId].quoted_unit_price,
        ) || 0
        const qty =
          parseFloat(
            field === 'available_quantity'
              ? value
              : updated[itemId].available_quantity,
          ) || 1
        updated[itemId].quoted_total_price = (unit * qty).toFixed(2)
      }
      if (field === 'alternative_unit_price' || field === 'alternative_quantity') {
        const unit = parseFloat(
          field === 'alternative_unit_price'
            ? value
            : updated[itemId].alternative_unit_price,
        ) || 0
        const qty =
          parseFloat(
            field === 'alternative_quantity'
              ? value
              : updated[itemId].alternative_quantity,
          ) || 1
        updated[itemId].alternative_total_price = (unit * qty).toFixed(2)
      }
      if (field === 'is_available' && value === true) {
        updated[itemId].quoted_unit_price = ''
        updated[itemId].quoted_total_price = ''
        updated[itemId].delivery_time = ''
        updated[itemId].vendor_comments = ''
      }
      if (field === 'has_alternative' && value === false) {
        updated[itemId].alternative_item_name = ''
        updated[itemId].alternative_unit_price = ''
        updated[itemId].alternative_total_price = ''
        updated[itemId].alternative_comments = ''
        updated[itemId].alternative_delivery_time = ''
        updated[itemId].alternative_quantity = ''
        updated[itemId].alternative_brand = ''
        updated[itemId].alternative_description = ''
        updated[itemId].alternative_reference = ''
      }
      return updated
    })
  }

  const handleQuoteChange = (field, value) =>
    setQuoteData(prev => ({ ...prev, [field]: value }))

  const calculateTotalQuote = () =>
    roundToTwoDecimals(
      Object.values(itemResponses).reduce((total, response) => {
        if (response.is_available && response.quoted_total_price) {
          return total + (parseFloat(response.quoted_total_price) || 0)
        }
        if (response.has_alternative && response.alternative_total_price) {
          return total + (parseFloat(response.alternative_total_price) || 0)
        }
        return total
      }, 0),
    )

  // FIX: currency is now stored as code (MAD/EUR/USD) directly, no lookup needed
  const getSelectedCurrencyCode = () => quoteData.currency || 'DH'

  const getTotalPaymentPercentage = () =>
    (parseFloat(quoteData.payment_at_command) || 0) +
    (parseFloat(quoteData.payment_at_delivery) || 0) +
    (parseFloat(quoteData.payment_at_receipt) || 0)

  const validateForm = () => {
    const allItemsResponded = Object.values(itemResponses).every(
      r =>
        (r.is_available === true && r.quoted_unit_price) ||
        (r.has_alternative && r.alternative_unit_price) ||
        r.is_available === false,
    )
    if (!allItemsResponded) {
      Modal.error({
        title: 'Erreur',
        content: 'Veuillez répondre à tous les articles de la demande',
      })
      return false
    }
    if (getTotalPaymentPercentage() !== 100) {
      Modal.error({
        title: 'Erreur',
        content: 'Le total des pourcentages de paiement doit être exactement 100%',
      })
      return false
    }
    if (!quoteData.currency) {
      Modal.error({
        title: 'Erreur',
        content: 'Veuillez sélectionner une devise',
      })
      return false
    }
    return true
  }

  const uploadAllDocuments = async () => {
    if (!quoteData.documents.length) return
    for (const doc of quoteData.documents) {
      const formData = new FormData()
      formData.append('file', doc.originFileObj)
      try {
        await axiosForm.post(
          `/authentication/api/rfq/${rfqId}/upload-document/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            }
          }
        )
      } catch (err) {
        Modal.error({
          title: "Erreur lors de l'upload",
          content: `Impossible de charger le document: ${doc.name}`,
        })
        throw err
      }
    }
  }

  const handleSubmitQuote = async () => {
    if (!validateForm()) return
    setSubmitting(true)
    try {
      await uploadAllDocuments()
      const items = rfqData.items.map(item => ({
        id: item.id,
        ...itemResponses[item.id]
      }))
      await axiosForm.post(
        `/authentication/api/rfq/${rfqId}/respond/`,
        {
          ...quoteData,
          items,
          total_quote_amount: calculateTotalQuote()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      setTimeout(() => {
        setSubmitting(false)
        setSubmitModalVisible(false)
        setSuccessModalVisible(true)
      }, 1200)
    } catch (err) {
      setSubmitting(false)
    }
  }

  const formatDate = d => (d ? dayjs(d).format('DD/MM/YYYY') : '')

  if (loading)
    return (
      <Card>
        <Spin spinning={loading} tip="Chargement...">
          <div style={{ minHeight: 120 }} />
        </Spin>
      </Card>
    )

  if (!rfqData) {
    return (
      <Card>
        <Text type="danger">Erreur de chargement de la demande de devis</Text>
        <Button
          type="primary"
          onClick={() => window.location.assign('/dashboard')}
          icon={<ArrowLeftOutlined />}
        >
          Retour
        </Button>
      </Card>
    )
  }

  return (
    // FIX: translate="no" prevents Google Translate from mutating DOM nodes
    // which causes React's removeChild crash on re-renders
    <div style={{ maxWidth: 1200, margin: '0 auto' }} translate="no">

      {/* Back Button */}
      <Button
        icon={<ArrowLeftOutlined />}
        style={{ marginBottom: 24, marginTop: 24, display: 'inline-block' }}
        onClick={() => window.location.assign('/dashboard')}
        size="middle"
      >
        Retour à la liste
      </Button>

      {/* RFQ Info */}
      <Card style={{ marginBottom: 30 }}>
        <Title level={4}>
          Répondre au Devis -{' '}
          <span style={{ color: '#0958d9' }}>{rfqData.rfq_number}</span>
        </Title>
        <Row gutter={32} style={{ marginTop: 10 }}>
          <Col md={12}>
            <div>
              <Text type="secondary">Fournisseur:</Text>{' '}
              <b>{rfqData.vendor_name}</b>
              <br />
              <Text type="secondary">Envoyé le:</Text>{' '}
              {formatDate(rfqData.sent_date)}
              <br />
              <Text type="secondary">Échéance:</Text>{' '}
              {formatDate(rfqData.due_date)}
            </div>
          </Col>
          <Col md={12}>
            <div style={{ textAlign: 'right', fontSize: 17 }}>
              {/* FIX: wrapped in <span> to prevent translation tools splitting text+expression nodes */}
              <b>
                <span>
                  Total estimé: {calculateTotalQuote().toLocaleString('fr-FR')}{' '}
                  {getSelectedCurrencyCode()}
                </span>
              </b>
            </div>
          </Col>
        </Row>

        {/* Show already attached documents */}
        {rfqData.documents && rfqData.documents.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <b>Documents déjà attachés:</b>
            <ul>
              {rfqData.documents.map(doc => (
                <li key={doc.id}>
                  <a href={doc.file} target="_blank" rel="noopener noreferrer">
                    {doc.original_filename || 'Document'}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* RFQ Items */}
      <div style={{ marginBottom: 30 }}>
        <Title level={5} style={{ marginBottom: 12 }}>
          <b>Articles demandés ({rfqData.items?.length || 0})</b>
        </Title>
        {rfqData.items?.map(item => (
          <Card
            key={item.id}
            hoverable
            style={{
              width: '100%',
              marginBottom: 18,
              background: '#fafcff',
              border: '1px solid #e6f7ff',
              boxShadow: '0 2px 8px #f0f1f3',
              display: 'block',
            }}
            styles={{ body: { padding: 18 } }}
          >
            <ArticleResponseCard
              item={item}
              response={itemResponses[item.id]}
              handleItemChange={handleItemChange}
              getSelectedCurrencyCode={getSelectedCurrencyCode}
            />
          </Card>
        ))}
      </div>

      {/* Quote Information */}
      <Card title="Informations sur le devis" style={{ marginBottom: 30 }}>
        <CommercialConditionsCard
          quoteData={quoteData}
          handleQuoteChange={handleQuoteChange}
          currencies={currencies}
          getTotalPaymentPercentage={getTotalPaymentPercentage}
          validityLabel="Validité de l'offre (jours)"
        />
        <Form.Item label="TVA (%)" style={{ marginBottom: 16 }}>
          <Input
            type="number"
            min={0}
            max={100}
            value={quoteData.tva}
            onChange={e => setQuoteData(q => ({ ...q, tva: Number(e.target.value) }))}
            style={{ width: 120 }}
          />
        </Form.Item>
        <Form.Item label="Documents (PDF, images, etc.)" style={{ marginBottom: 16 }}>
          <Upload
            multiple
            beforeUpload={() => false}
            fileList={quoteData.documents}
            onChange={({ fileList }) => setQuoteData(q => ({ ...q, documents: fileList }))}
          >
            <Button icon={<UploadOutlined />}>Ajouter des documents</Button>
          </Upload>
        </Form.Item>
        <Divider />
        <Form.Item label="Notes générales">
          <Input.TextArea
            rows={3}
            value={quoteData.vendor_notes}
            onChange={e => handleQuoteChange('vendor_notes', e.target.value)}
          />
        </Form.Item>
      </Card>

      {/* Submit Button */}
      <div style={{ textAlign: 'right', marginBottom: 36 }}>
        <Button
          type="primary"
          size="large"
          icon={<SendOutlined />}
          onClick={() => setSubmitModalVisible(true)}
          loading={submitting}
        >
          Soumettre le devis
        </Button>
      </div>

      {/* Confirm Modal */}
      <Modal
        open={submitModalVisible}
        onCancel={() => setSubmitModalVisible(false)}
        onOk={handleSubmitQuote}
        okText="Confirmer"
        cancelText="Annuler"
        confirmLoading={submitting}
        title="Confirmer la soumission"
      >
        <p>Confirmer la soumission de votre devis ?</p>
        {/* FIX: all mixed text+expression nodes wrapped in <span> */}
        <div style={{ fontWeight: 600, fontSize: 18 }}>
          <span>
            Total: {calculateTotalQuote().toLocaleString('fr-FR')}{' '}
            {getSelectedCurrencyCode()}
          </span>
        </div>
        <div><span>TVA: {quoteData.tva} %</span></div>
        <div><span>Nombre de documents joints: {quoteData.documents.length}</span></div>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={successModalVisible}
        onCancel={() => {
          setSuccessModalVisible(false)
          window.location.assign('/dashboard')
        }}
        footer={[
          <Button
            type="primary"
            key="ok"
            onClick={() => {
              setSuccessModalVisible(false)
              window.location.assign('/dashboard')
            }}
          >
            Retour à la liste
          </Button>,
        ]}
        title="Devis soumis avec succès"
      >
        <p>Votre devis a été soumis avec succès.</p>
      </Modal>
    </div>
  )
}

export default SupplierQuotationResponse
